<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <h3 class="text-lg font-semibold mb-4 flex items-center">
      <i class="fas fa-calculator mr-2 text-emerald-500"></i>
      Comparable Data Calculator
    </h3>
    
    <!-- API Endpoint Selection -->
    <div class="mb-4 p-3 bg-gray-50 rounded-lg border">
      <label class="block text-sm font-medium text-gray-700 mb-2">API Endpoint</label>
      <div class="flex space-x-4">
        <label class="flex items-center">
          <input 
            v-model="apiEndpoint" 
            type="radio" 
            value="local" 
            class="mr-2 text-emerald-500 focus:ring-emerald-500"
          />
          <span class="text-sm">Local Laravel (balanced-lines/calculate-stats — /api or /api/v2 when VITE_API_V2_BALANCED_LINES_PROJECTIONS is set)</span>
        </label>
        <label class="flex items-center">
          <input 
            v-model="apiEndpoint" 
            type="radio" 
            value="external" 
            class="mr-2 text-emerald-500 focus:ring-emerald-500"
          />
          <span class="text-sm">External API</span>
        </label>
      </div>
    </div>

    <!-- Market Type Selection -->
    <div class="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <label class="block text-sm font-medium text-gray-700 mb-2">Market Type</label>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label class="flex items-center p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
               :class="{ 'border-emerald-500 bg-emerald-50': formData.marketType === 'points' }">
          <input 
            v-model="formData.marketType" 
            type="radio" 
            value="points" 
            class="mr-3 text-emerald-500 focus:ring-emerald-500"
          />
          <div>
            <div class="font-medium text-gray-900">Points</div>
            <div class="text-sm text-gray-500">Player scoring statistics</div>
          </div>
        </label>
        
        <label class="flex items-center p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
               :class="{ 'border-emerald-500 bg-emerald-50': formData.marketType === 'total_rebounds' }">
          <input 
            v-model="formData.marketType" 
            type="radio" 
            value="total_rebounds" 
            class="mr-3 text-emerald-500 focus:ring-emerald-500"
          />
          <div>
            <div class="font-medium text-gray-900">Total Rebounds</div>
            <div class="text-sm text-gray-500">Offensive + defensive rebounds</div>
          </div>
        </label>
        
        <label class="flex items-center p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
               :class="{ 'border-emerald-500 bg-emerald-50': formData.marketType === 'assists' }">
          <input 
            v-model="formData.marketType" 
            type="radio" 
            value="assists" 
            class="mr-3 text-emerald-500 focus:ring-emerald-500"
          />
          <div>
            <div class="font-medium text-gray-900">Assists</div>
            <div class="text-sm text-gray-500">Player assist statistics</div>
          </div>
        </label>
      </div>
    </div>

    <!-- Input Form -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">{{ getPerThirtyLabel() }} Range</label>
        <div class="flex space-x-2">
          <input 
            v-model.number="formData.perThirtyStart"
            type="number"
            step="0.1"
            placeholder="Start"
            class="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <input 
            v-model.number="formData.perThirtyEnd"
            type="number"
            step="0.1"
            placeholder="End"
            class="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>
      
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Avg Minutes Per Game Range</label>
        <div class="flex space-x-2">
          <input 
            v-model.number="formData.minutesStart"
            type="number"
            placeholder="Start"
            class="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <input 
            v-model.number="formData.minutesEnd"
            type="number"
            placeholder="End"
            class="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>
      
      <div class="flex items-end">
        <button 
          @click="calculateStats"
          :disabled="loading || !isFormValid"
          class="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <i class="fas fa-calculator mr-2"></i>
          {{ loading ? 'Calculating...' : 'Calculate Stats' }}
        </button>
      </div>
    </div>
    
    <!-- Results Display -->
    <div v-if="results" class="mt-6 space-y-6">
      <!-- Header -->
      <div class="bg-gradient-to-r from-emerald-500 to-blue-500 text-white p-4 rounded-lg">
        <h4 class="text-lg font-semibold flex items-center">
          <i class="fas fa-chart-line mr-2"></i>
          Calculation Results
        </h4>
        <p class="text-emerald-100 text-sm mt-1">
          Market Type: {{ results.market_type || 'N/A' }} | 
          API: {{ apiEndpoint === 'local' ? 'Local Laravel' : 'External API' }}
        </p>
      </div>

      <!-- Query Parameters Used -->
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <h5 class="font-semibold text-gray-700 mb-3 flex items-center">
          <i class="fas fa-filter mr-2 text-gray-500"></i>
          Query Parameters Used
        </h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div class="bg-gray-50 p-3 rounded-lg">
             <div class="text-sm font-medium text-gray-600 mb-1">{{ getPerThirtyLabel() }} Range</div>
             <div class="text-lg">
               <span class="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-sm">
                 {{ results.range?.per_thirty?.start || formData.perThirtyStart }} - {{ results.range?.per_thirty?.end || formData.perThirtyEnd }}
               </span>
             </div>
           </div>
          <div class="bg-gray-50 p-3 rounded-lg">
            <div class="text-sm font-medium text-gray-600 mb-1">Minutes Per Game Range</div>
            <div class="text-lg">
              <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                {{ results.range?.minutes?.start || formData.minutesStart }} - {{ results.range?.minutes?.end || formData.minutesEnd }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                 <div class="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
           <div class="flex items-center justify-between mb-2">
             <div class="text-sm font-medium text-gray-600">{{ getStatLabel() }}</div>
             <div class="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
               <i class="fas fa-chart-bar text-emerald-600 text-sm"></i>
             </div>
           </div>
                      <div class="text-2xl font-bold text-emerald-600">
             {{ getMeanValue() }}
           </div>
           <div class="text-xs text-gray-500 mt-1">Average performance</div>
         </div>

         <div class="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
           <div class="flex items-center justify-between mb-2">
             <div class="text-sm font-medium text-gray-600">Standard Deviation</div>
             <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
               <i class="fas fa-wave-square text-blue-600 text-sm"></i>
             </div>
           </div>
           <div class="text-2xl font-bold text-blue-600">
             {{ getStdDevValue() }}
           </div>
           <div class="text-xs text-gray-500 mt-1">Performance variability</div>
         </div>

        <div class="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between mb-2">
            <div class="text-sm font-medium text-gray-600">Total Games</div>
            <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <i class="fas fa-hashtag text-purple-600 text-sm"></i>
            </div>
          </div>
          <div class="text-2xl font-bold text-purple-600">
            {{ results.stats?.total_games?.toLocaleString() || 0 }}
          </div>
          <div class="text-xs text-gray-500 mt-1">Sample size</div>
        </div>
      </div>

      <!-- Statistical Analysis -->
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <h5 class="font-semibold text-gray-700 mb-3 flex items-center">
          <i class="fas fa-analytics mr-2 text-indigo-500"></i>
          Statistical Analysis
        </h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <div class="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span class="text-sm text-gray-600">Mean ± 1 Std Dev</span>
              <span class="text-sm font-medium">
                {{ getStatRange(1) }}
              </span>
            </div>
            <div class="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span class="text-sm text-gray-600">Mean ± 2 Std Dev</span>
              <span class="text-sm font-medium">
                {{ getStatRange(2) }}
              </span>
            </div>
          </div>
          <div class="space-y-2">
            <div class="flex justify-between items-center p-2 bg-emerald-50 rounded">
              <span class="text-sm text-gray-600">Coefficient of Variation</span>
              <span class="text-sm font-medium text-emerald-700">
                {{ getCoefficientOfVariation() }}%
              </span>
            </div>
            <div class="flex justify-between items-center p-2 bg-blue-50 rounded">
              <span class="text-sm text-gray-600">Reliability Score</span>
              <span class="text-sm font-medium text-blue-700">
                {{ getReliabilityScore() }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Error Display -->
    <div v-if="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-center">
        <i class="fas fa-exclamation-triangle mr-2 text-red-500"></i>
        <span class="text-red-700">{{ error }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import axios from '../axios'
import { apiBalancedProjectionsPath, useBalancedProjectionsV2 } from '../config/balancedProjectionsApi.js'

// Props
const props = defineProps({
  connectionStatus: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['log'])

// Reactive data
const loading = ref(false)
const results = ref(null)
const error = ref(null)
const apiEndpoint = ref('local')

const formData = ref({
  marketType: 'points',
  perThirtyStart: null,
  perThirtyEnd: null,
  minutesStart: null,
  minutesEnd: null
})

// Computed
const isFormValid = computed(() => {
  return formData.value.marketType &&
         formData.value.perThirtyStart !== null && 
         formData.value.perThirtyEnd !== null &&
         formData.value.minutesStart !== null && 
         formData.value.minutesEnd !== null
})

// Methods
const calculateStats = async () => {
  if (apiEndpoint.value === 'local' && !props.connectionStatus) {
    error.value = 'Not connected to database'
    emit('log', 'Error: Not connected to database', 'error')
    return
  }

  if (!isFormValid.value) {
    error.value = 'Please fill in all required fields'
    emit('log', 'Error: Missing required fields', 'error')
    return
  }

  loading.value = true
  error.value = null
  results.value = null

  const remoteBase = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || 'https://api.v2.test.oddscomposer.io'
  const remotePath = useBalancedProjectionsV2()
    ? '/api/v2/balanced-lines/calculate-stats'
    : '/api/balanced-lines/calculate-stats'
  const endpoint =
    apiEndpoint.value === 'local'
      ? apiBalancedProjectionsPath('balanced-lines/calculate-stats')
      : `${remoteBase}${remotePath}`

  emit('log', `Calculating comparable data stats via ${apiEndpoint.value} API...`, 'info')

  try {
    const config = {
      method: 'POST',
      url: endpoint,
      data: formData.value,
      headers: {
        'Content-Type': 'application/json'
      }
    }

    // Add CSRF token only for local requests
    if (apiEndpoint.value === 'local') {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken
      }
    }

    const response = await axios(config)
    results.value = response.data
    emit('log', `Stats calculated successfully via ${apiEndpoint.value} API`, 'success')
    
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred'
    error.value = `Failed to calculate stats: ${errorMessage}`
    emit('log', `Error calculating stats via ${apiEndpoint.value} API: ${errorMessage}`, 'error')
  } finally {
    loading.value = false
  }
}

// Clear form
const clearForm = () => {
  formData.value = {
    marketType: 'points',
    perThirtyStart: null,
    perThirtyEnd: null,
    minutesStart: null,
    minutesEnd: null
  }
  results.value = null
  error.value = null
  emit('log', 'Form cleared', 'info')
}

// Helper method for dynamic label
const getPerThirtyLabel = () => {
  const marketLabels = {
    'points': 'Per Thirty Points',
    'total_rebounds': 'Per Thirty Rebounds',
    'assists': 'Per Thirty Assists'
  }
  return marketLabels[formData.value.marketType] || 'Per Thirty Points'
}

const getStatLabel = () => {
  const statLabels = {
    'points': 'Mean Points',
    'total_rebounds': 'Mean Rebounds',
    'assists': 'Mean Assists'
  }
  return statLabels[formData.value.marketType] || 'Mean Points'
}

const getMeanValue = () => {
  if (!results.value?.stats) return 'N/A'
  
  const fieldNames = {
    'points': 'mean_points',
    'total_rebounds': 'mean_total_rebounds',
    'assists': 'mean_assists'
  }
  
  const fieldName = fieldNames[formData.value.marketType] || 'mean_points'
  const value = results.value.stats[fieldName]
  
  return value ? Number(value).toFixed(4) : 'N/A'
}

const getStdDevValue = () => {
  if (!results.value?.stats) return 'N/A'
  
  const fieldNames = {
    'points': 'stddev_points',
    'total_rebounds': 'stddev_total_rebounds',
    'assists': 'stddev_assists'
  }
  
  const fieldName = fieldNames[formData.value.marketType] || 'stddev_points'
  const value = results.value.stats[fieldName]
  
  return value ? Number(value).toFixed(4) : 'N/A'
}

// Statistical analysis helper methods
const getStatRange = (stdDevMultiplier) => {
  const meanValue = getMeanValueRaw()
  const stdDevValue = getStdDevValueRaw()
  
  if (meanValue === null || stdDevValue === null) {
    return 'N/A'
  }
  
  const lowerBound = meanValue - (stdDevMultiplier * stdDevValue)
  const upperBound = meanValue + (stdDevMultiplier * stdDevValue)
  
  return `${lowerBound.toFixed(1)} - ${upperBound.toFixed(1)}`
}

const getCoefficientOfVariation = () => {
  const meanValue = getMeanValueRaw()
  const stdDevValue = getStdDevValueRaw()
  
  if (meanValue === null || stdDevValue === null || meanValue === 0) {
    return 'N/A'
  }
  
  const cv = (stdDevValue / meanValue) * 100
  return cv.toFixed(1)
}

// Helper methods to get raw numeric values
const getMeanValueRaw = () => {
  if (!results.value?.stats) return null
  
  const fieldNames = {
    'points': 'mean_points',
    'total_rebounds': 'mean_total_rebounds',
    'assists': 'mean_assists'
  }
  
  const fieldName = fieldNames[formData.value.marketType] || 'mean_points'
  const value = results.value.stats[fieldName]
  
  return value ? Number(value) : null
}

const getStdDevValueRaw = () => {
  if (!results.value?.stats) return null
  
  const fieldNames = {
    'points': 'stddev_points',
    'total_rebounds': 'stddev_total_rebounds',
    'assists': 'stddev_assists'
  }
  
  const fieldName = fieldNames[formData.value.marketType] || 'stddev_points'
  const value = results.value.stats[fieldName]
  
  return value ? Number(value) : null
}

const getReliabilityScore = () => {
  if (!results.value?.stats?.total_games) {
    return 'N/A'
  }
  
  const totalGames = results.value.stats.total_games
  const cv = getCoefficientOfVariation()
  
  if (cv === 'N/A') return 'N/A'
  
  // Simple reliability scoring based on sample size and consistency
  let score = 'Low'
  if (totalGames >= 100 && Number(cv) < 50) {
    score = 'High'
  } else if (totalGames >= 50 && Number(cv) < 70) {
    score = 'Medium'
  }
  
  return score
}

// Expose methods for parent component
defineExpose({
  clearForm
})
</script> 